import React, { useState } from "react";
import { toast } from "sonner";
import { RotateCcw, ShieldAlert } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

function ResetDialog({ open, onOpenChange, onDone }) {
  const [confirm, setConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/demo/reset", { confirm, password });
      toast.success(`${data.message} ${data.total} record dihapus.`);
      onDone(data); onOpenChange(false); setConfirm(""); setPassword("");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="demo-reset-dialog">
        <DialogHeader>
          <DialogTitle>Reset seluruh data latihan?</DialogTitle>
          <DialogDescription>
            Semua transaksi dan data master operasional yang dibuat di Mode Demo akan dihapus dan tidak dapat dikembalikan. Data production tidak akan terpengaruh.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Ketik <b>RESET DEMO</b></Label>
            <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="demo-reset-confirm-input" /></div>
          <div><Label>Kata sandi konfirmasi</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="demo-reset-password-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="demo-reset-cancel-btn">Batal</Button>
          <Button variant="destructive" disabled={busy || confirm.trim() !== "RESET DEMO" || !password} onClick={run} data-testid="demo-reset-submit-btn">
            {busy ? "Mereset..." : "Reset Data Demo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function DemoSettingsPage() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [last, setLast] = useState(null);
  if (!user?.is_demo) {
    return <div className="text-slate-500" data-testid="demo-settings-unavailable">Halaman ini hanya tersedia di Mode Demo.</div>;
  }
  return (
    <div className="space-y-6 page-fade" data-testid="demo-settings-page">
      <div><h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Pengaturan Demo</h1>
        <p className="text-slate-500 mt-2">Lingkungan latihan terisolasi. Data production tidak dapat diakses dari sini.</p></div>
      <Card>
        <CardHeader><CardTitle className="font-display flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-amber-600" /> Reset Data Demo</CardTitle></CardHeader>
        <CardContent className="space-y-4 text-sm">
          <p>Menghapus seluruh transaksi dan master operasional latihan, lalu memulihkan konfigurasi dasar (COA, mapping, pajak, workflow, mata uang, pengaturan sistem).</p>
          <Button variant="destructive" onClick={() => setOpen(true)} data-testid="demo-reset-open-btn">
            <RotateCcw className="h-4 w-4 mr-2" /> Reset Data Demo
          </Button>
          {last && <p className="text-slate-600" data-testid="demo-reset-result">Reset terakhir: {last.total} record dihapus dari {Object.keys(last.deleted).length} collection demo.</p>}
        </CardContent>
      </Card>
      <ResetDialog open={open} onOpenChange={setOpen} onDone={setLast} />
    </div>
  );
}
